import { CalendarDays, DollarSign, Hash, KeyRound, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Chip } from 'vnetwork-ui'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { CONTRACT_STATUS } from '@/modules/Contract/constants'
import { formatCurrency, formatDate } from '@/utils/function'
import { paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getContractPartner } from '../../services'
import CreatePartnerSubcontract from '../../shared/Buttons/CreateSubcontract'
import AttachmentComponent from '@/modules/Contract/components/Detail/Attachment'
import SubcontractModules from '@/modules/Contract/modules/Subcontract'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'

/** Info item in the detail grid */
const InfoItem = ({
    icon,
    label,
    value,
    className,
}: {
    icon?: React.ReactNode
    label: string
    value?: React.ReactNode
    className?: string
}) => (
    <div className={className}>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
            {icon}
            {label}
        </div>
        {value ? (
            <div className="text-sm font-medium">{value}</div>
        ) : (
            <div className="text-sm italic text-gray-400">---</div>
        )}
    </div>
)

const Detail = () => {
    const { t } = useTranslation('contract')
    const { contractID } = useParams<{ contractID: string }>()

    const { data: contract, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CONTRACT_DETAIL, Number(contractID)],
        queryFn: () => getContractPartner(Number(contractID) || 0),
    })

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const data = contract?.result
    const status = CONTRACT_STATUS(t).find(
        (s) => s.value === data?.contract_status
    )

    return (
        <div className="space-y-2">
            <SubHeaders
                breadcrumbs={[
                    {
                        path: paths.partnerContractList,
                        title: t('contract-partners'),
                    },
                    {
                        path: '',
                        title: `${data?.contract_number_id}`,
                    },
                ]}
                components={[
                    <CreatePartnerSubcontract
                        contractID={Number(contractID)}
                    />,
                ]}
            />

            {/* Contract Information */}
            <Paper className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <InfoItem
                        icon={<Hash className="w-3.5 h-3.5" />}
                        label={t('contract_number_id')}
                        value={
                            <span className="text-primary font-semibold">
                                {data?.contract_number_id}
                            </span>
                        }
                    />
                    <InfoItem
                        label={t('contract_status')}
                        value={
                            status ? (
                                <Chip
                                    label={status.label}
                                    size="small"
                                    variant="contained"
                                    color={status.color as any}
                                />
                            ) : (
                                data?.contract_status
                            )
                        }
                    />
                    <InfoItem
                        icon={<DollarSign className="w-3.5 h-3.5" />}
                        label={t('contract_value')}
                        value={
                            data?.contract_value
                                ? formatCurrency(data.contract_value)
                                : undefined
                        }
                    />
                    <InfoItem
                        icon={<User className="w-3.5 h-3.5" />}
                        label={t('contract_employee')}
                        value={data?.employee_name}
                    />
                    <InfoItem
                        icon={<CalendarDays className="w-3.5 h-3.5" />}
                        label={t('contract_effective_date')}
                        value={
                            data?.contract_effective_date
                                ? formatDate(data.contract_effective_date)
                                : undefined
                        }
                    />
                    <InfoItem
                        icon={<CalendarDays className="w-3.5 h-3.5" />}
                        label={t('contract_expiry_date')}
                        value={
                            data?.contract_expiry_date
                                ? formatDate(data.contract_expiry_date)
                                : undefined
                        }
                    />
                    <InfoItem
                        icon={<KeyRound className="w-3.5 h-3.5" />}
                        label={t('contract_key')}
                        value={data?.contract_key}
                        className="md:col-span-2"
                    />
                    {data?.contract_note && (
                        <div className="col-span-full">
                            <div className="text-xs text-muted-foreground mb-1">
                                {t('contract_note')}
                            </div>
                            <div className="text-sm text-muted-foreground italic">
                                {data.contract_note}
                            </div>
                        </div>
                    )}
                </div>
            </Paper>

            <AttachmentComponent
                contractID={Number(contractID)}
                attachments={data?.attachments}
            />
            <SubcontractModules.List
                contractID={Number(contractID)}
                contractType="partner"
            />
        </div>
    )
}

export const DetailPartner = () => <Detail />
